import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { doc } from "firebase/firestore";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { db } from "../../../lib/firebase";
import { useFirestoreDocOnce } from "../../../hooks/useFirestoreDocOnce";
import { TravelForm } from "./TravelForm";
import type { Audience, TravelItem } from "@event-planner/shared";

export function TravelEditPage() {
  const { travelItemId } = useParams<{ travelItemId: string }>();

  const docRef = useMemo(
    () => (travelItemId ? doc(db, "travelItems", travelItemId) : null),
    [travelItemId],
  );
  const { data: item, loading } = useFirestoreDocOnce<TravelItem>(docRef);

  // Load the linked audience so we can resolve the picker's initial value.
  // Mirrors SessionEditPage: named audience → [audienceId]; anonymous
  // composition → members.includedAudienceIds.
  const audienceRef = useMemo(
    () => (item?.audienceId ? doc(db, "audiences", item.audienceId) : null),
    [item?.audienceId],
  );
  const { data: audience, loading: audienceLoading } =
    useFirestoreDocOnce<Audience>(audienceRef);

  if (loading || (item && audienceLoading)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" component="h1">
          Travel item not found
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This travel item may have been deleted.
        </Typography>
      </Box>
    );
  }

  const initialPickedAudienceIds = audience
    ? audience.name === null && audience.system === false
      ? // Anonymous composition: spread its include list back into the
        // picker so the organizer sees what they originally picked.
        audience.members.includedAudienceIds
      : // Named (custom group / role audience) or personal audience —
        // the picker shows the single referenced ID.
        [audience.id]
    : [];

  return (
    <TravelForm
      mode="edit"
      initialData={item}
      initialPickedAudienceIds={initialPickedAudienceIds}
    />
  );
}
